import React, { FC, useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { AddButton as ButtonWithIcon } from '@td-client-frontend/common';
import { ChevronLeft } from '@td-client-frontend/icons';
import { DatePicker, TextField, Select } from '../../../components/ReactHookForm';
import { GroupGridRow } from '../GroupHelperComponents';
import { ViewPlanNumbersModal } from './ViewPlanNumbersModal';
import { Products } from './Products';
import FutureProducts from './FutureProducts';
import { useGroupContext } from '../../../context/GroupContext';
import GroupCodesModal from './GroupCodes/GroupCodesModal';
import { eligibilityTypes, language } from '../../../data';
import { GroupCodes as GroupCodesInterface, PlanCodePackage } from '../../../@types/Groups';
import GroupCodes from './GroupCodes/GroupCodes';

enum ModalType {
  FUTURE_PRODUCTS = 'futureProducts',
  PLAN_NUMBERS = 'planNumbers',
  PLAN_CODES = 'planCodes',
  CLASS_CODES = 'classCodes'
}

interface ManageGroupProps {
  name: string;
  onBackClick: () => void;
}

const ManageGroup: FC<ManageGroupProps> = ({ name, onBackClick }) => {
  const [activeModalType, setActiveModalType] = useState<ModalType | null>(null);
  const handleCloseModal = useCallback(() => setActiveModalType(null), []);

  const { type } = useGroupContext();
  const { getValues, watch, setValue } = useFormContext();

  const [selectedPlanNumber, planNumbers]: [string, PlanCodePackage[]] = watch([
    `${name}.selectedPlanNumber`,
    `${name}.planCodes`
  ]);

  const planNumbersIndex: number = useMemo(
    () => planNumbers?.findIndex((planNumber: PlanCodePackage) => planNumber.code === selectedPlanNumber),
    [planNumbers, selectedPlanNumber]
  );
  const planNumbersOptions: { value: string; label: string }[] = useMemo(
    () => (planNumbers || []).map(({ code }: PlanCodePackage) => ({ value: code, label: code })),
    [planNumbers]
  );

  const handleSaveClassCodes = useCallback(
    (data: GroupCodesInterface[]) =>
      setValue(`${name}.planCodes.${planNumbersIndex}.classCodes`, data, { shouldValidate: true }),
    [name, planNumbersIndex, setValue]
  );

  const handleSaveBpCodes = useCallback(
    (data: GroupCodesInterface[]) =>
      setValue(`${name}.planCodes.${planNumbersIndex}.bpCodes`, data, { shouldValidate: true }),
    [name, planNumbersIndex, setValue]
  );

  const handlePlanNumbersChange = (value: PlanCodePackage[]) => {
    setValue(`${name}.planCodes`, value, { shouldValidate: true });
    setValue(`${name}.selectedPlanNumber`, value[0]?.code);
  };

  const modals: Record<ModalType, React.ReactNode> = {
    [ModalType.FUTURE_PRODUCTS]: (
      <FutureProducts
        onClose={handleCloseModal}
        path={`${name}.futureProducts`}
        initialData={getValues(`${name}.futureProducts`)}
      />
    ),
    [ModalType.PLAN_NUMBERS]: (
      <ViewPlanNumbersModal
        onClose={handleCloseModal}
        planCodesPath={`${name}.planCodes`}
        onChange={handlePlanNumbersChange}
      />
    ),
    [ModalType.CLASS_CODES]: (
      <GroupCodesModal
        onClose={handleCloseModal}
        label="plan"
        defaultValues={getValues(`${name}.planCodes.${planNumbersIndex}.classCodes`) || []}
        onSubmit={handleSaveClassCodes}
        path={`${name}.planCodes.${planNumbersIndex}.classCodes`}
      />
    ),
    [ModalType.PLAN_CODES]: (
      <GroupCodesModal
        onClose={handleCloseModal}
        label="benefit"
        defaultValues={getValues(`${name}.planCodes.${planNumbersIndex}.bpCodes`) || []}
        onSubmit={handleSaveBpCodes}
        path={`${name}.planCodes.${planNumbersIndex}.bpCodes`}
      />
    )
  };

  return (
    <>
      <ButtonWithIcon label="back" Icon={<ChevronLeft className="text-primary-500 w-5 h-5" />} onClick={onBackClick} />
      <GroupGridRow>
        <TextField name={`${name}.name`} label="Plan Name" wrapperClasses="col-span-3" required />
        <DatePicker
          name={`${name}.start_dt`}
          label="Effective Date"
          placeholder="MM/DD/YYYY"
          wrapperClasses="col-span-3"
          required
        />
        <DatePicker
          name={`${name}.end_dt`}
          label="Termination Date"
          wrapperClasses="col-span-3"
          placeholder="MM/DD/YYYY"
        />
      </GroupGridRow>
      <GroupGridRow>
        <Select
          name={`${name}.selectedPlanNumber`}
          label="Plan Number"
          options={planNumbersOptions}
          labelBtnType="View"
          labelBtn={() => setActiveModalType(ModalType.PLAN_NUMBERS)}
          wrapperClasses="col-span-3"
          placeholder="eg 1234-9876"
          required
        />
        <GroupCodes
          label="Plan code *"
          path={`${name}.planCodes.${planNumbersIndex}.classCodes`}
          onLabelClick={() => setActiveModalType(ModalType.CLASS_CODES)}
        />
        <GroupCodes
          label="Benefit Plans (BP) *"
          path={`${name}.planCodes.${planNumbersIndex}.bpCodes`}
          onLabelClick={() => setActiveModalType(ModalType.PLAN_CODES)}
        />
      </GroupGridRow>
      <GroupGridRow>
        <Select
          name={`${name}.reg_type`}
          label="Eligibility Type"
          options={eligibilityTypes}
          wrapperClasses="col-span-3"
        />
        <Select name={`${name}.language`} label="Welcome Kit Language" options={language} wrapperClasses="col-span-3" />
      </GroupGridRow>
      <Products name={`${name}.products`} />

      {type !== 'draft' && (
        <GroupGridRow>
          <button
            className="btn btn-link focus:outline-none focus-visible:ring col-span-3"
            type="button"
            onClick={() => setActiveModalType(ModalType.FUTURE_PRODUCTS)}
          >
            Schedule price update
          </button>
        </GroupGridRow>
      )}

      {/* Modals */}
      {activeModalType && modals[activeModalType]}
    </>
  );
};

export default ManageGroup;
